import { requestStatus } from 'src/utils/formRequestDecisions';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Box, Typography, Button, Stack, alpha } from '@mui/material';
import Iconify from '../../../components/Iconify';

// ─── Date helpers ──────────────────────────────────────────────────────────────
const parseApiDate = (dateStr) => {
    if (!dateStr) return null;
    const cleaned = dateStr.replace(/\.\d+/, '');
    const parsed = moment.utc(cleaned, 'DD-MM-YYYY HH:mm:ssZ');
    if (parsed.isValid()) return parsed.local();
    const iso = moment(dateStr);
    return iso.isValid() ? iso : null;
};

const formatDate = (dateStr) => {
    const m = parseApiDate(dateStr);
    return m ? m.format('DD MMM YYYY, h:mm A') : '—';
};

const fromNow = (dateStr) => {
    const m = parseApiDate(dateStr);
    return m ? m.fromNow() : '';
};

// ─── Action button ────────────────────────────────────────────────────────────
function ActionBtn({ icon, label, onClick, color = '#078DEE' }) {
    return (
        <Button
            size="small"
            variant="outlined"
            startIcon={<Iconify icon={icon} width={14} />}
            onClick={onClick}
            sx={{
                borderRadius: '7px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '12px',
                py: 0.5,
                px: 1.25,
                borderColor: alpha(color, 0.4),
                color,
                '&:hover': { bgcolor: alpha(color, 0.06), borderColor: color },
            }}
        >
            {label}
        </Button>
    );
}

ActionBtn.propTypes = {
    icon:    PropTypes.string.isRequired,
    label:   PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    color:   PropTypes.string,
};

ActionBtn.defaultProps = { color: '#078DEE' };

// ─────────────────────────────────────────────────────────────────────────────

export default function SequenceStepDetail({ step, onResend, onCopyLink, onViewSubmission, onRemind }) {
    let status = requestStatus(step);
    if (step?.status === 'CURRENT' && requestStatus({ ...step, status: 'PENDING' }) === 'EXPIRED') status = 'EXPIRED';

    if (status === 'SUBMITTED') {
        const submittedAt = step?.submitted_at || step?.updated_at;
        const signerName = step?.submitted_by_name
            || (step?.lead ? `${step.lead.first_name || ''} ${step.lead.last_name || ''}`.trim() : '')
            || 'Patient';
        return (
            <Box sx={{ pt: 1.5, pl: 0.5 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Iconify icon="eva:person-fill" width={13} sx={{ color: '#00AB55' }} />
                    <Typography variant="caption" sx={{ color: '#00AB55', fontWeight: 600, fontSize: '12px' }}>
                        Signed by {signerName}
                    </Typography>
                </Stack>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '12px', display: 'block', mb: 1 }}>
                    Submitted {formatDate(submittedAt)}
                </Typography>
                {onResend && <ActionBtn icon="eva:plus-fill" label="Request a new response" onClick={() => onResend(step)} />}
                {onViewSubmission && (
                    <ActionBtn
                        icon="eva:eye-fill"
                        label="Open in viewer"
                        onClick={() => onViewSubmission(step)}
                        color="#00AB55"
                    />
                )}
            </Box>
        );
    }

    if (status === 'OPENED' || status === 'IN_PROGRESS') {
        const openedAt = step.opened_at || step.updated_at;
        return (
            <Box sx={{ pt: 1.5, pl: 0.5 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '12px', display: 'block', mb: 1 }}>
                    Opened {openedAt ? fromNow(openedAt) : '—'}
                    {openedAt ? ` · ${formatDate(openedAt)}` : ''}
                </Typography>
                <Stack direction="row" spacing={1}>
                    {onRemind && (
                        <ActionBtn
                            icon="eva:bell-fill"
                            label="Remind using existing link"
                            onClick={() => onRemind(step)}
                            color="#FFAB00"
                        />
                    )}
                    {onCopyLink && (
                        <ActionBtn
                            icon="eva:copy-fill"
                            label="Copy link"
                            onClick={() => onCopyLink(step)}
                        />
                    )}
                </Stack>
            </Box>
        );
    }

    if (status === 'PENDING') {
        // Determine which form name must be completed first
        const prevFormName = step._prevFormName || null;
        return (
            <Box sx={{ pt: 1.5, pl: 0.5 }}>
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '12px' }}>
                    {prevFormName
                        ? `Will be available after "${prevFormName}" is completed`
                        : 'Waiting for earlier steps to be completed'}
                </Typography>
            </Box>
        );
    }

    if (status === 'EXPIRED') {
        const expiresAt = step.expires_at || step.expiry_date;
        return (
            <Box sx={{ pt: 1.5, pl: 0.5 }}>
                <Typography variant="caption" sx={{ color: '#FF4842', fontSize: '12px', display: 'block', mb: 1 }}>
                    Expired{expiresAt ? ` on ${formatDate(expiresAt)}` : ''}
                </Typography>
                {onResend && (
                    <ActionBtn
                        icon="eva:refresh-fill"
                        label="Replace expired link"
                        onClick={() => onResend(step)}
                        color="#FF4842"
                    />
                )}
            </Box>
        );
    }

    // CURRENT / default — link generated, awaiting patient
    const createdAt = step.last_sent_at || step.created_at;
    return (
        <Box sx={{ pt: 1.5, pl: 0.5 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '12px', display: 'block', mb: 1 }}>
                {step.last_sent_at ? 'Last sent' : 'Created'}{createdAt ? ` · ${fromNow(createdAt)}` : ''}
            </Typography>
            <Stack direction="row" spacing={1}>
                {onCopyLink && (
                    <ActionBtn
                        icon="eva:copy-fill"
                        label="Copy link"
                        onClick={() => onCopyLink(step)}
                    />
                )}
                {onRemind && (
                    <ActionBtn
                        icon="eva:paper-plane-fill"
                        label="Remind using existing link"
                        onClick={() => onRemind(step)}
                        color="#855CF8"
                    />
                )}
            </Stack>
        </Box>
    );
}

SequenceStepDetail.propTypes = {
    step:             PropTypes.object.isRequired,
    onResend:         PropTypes.func,
    onCopyLink:       PropTypes.func,
    onViewSubmission: PropTypes.func,
    onRemind:         PropTypes.func,
};

SequenceStepDetail.defaultProps = {
    onResend:         null,
    onCopyLink:       null,
    onViewSubmission: null,
    onRemind:         null,
};
